import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Truck, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { money, COUNTRIES } from "@/lib/orderStatus";

let _rowKey = 0;
const nextKey = () => `crow-${++_rowKey}`;

const CARRIERS_BY_COUNTRY = {
  CA: [
    { id: "poste_canada", label: "Poste Canada", desc: "Calcul automatique selon le poids", auto: true },
    { id: "uber_direct", label: "Uber Direct", desc: "Livraison à la demande — bientôt disponible", soon: true },
  ],
  FR: [
    { id: "colissimo", label: "Colissimo (La Poste)", desc: "Calcul automatique selon le poids", auto: true },
    { id: "uber_direct", label: "Uber Direct", desc: "Livraison à la demande — bientôt disponible", soon: true },
  ],
};
const FLAGSHIP_PARTNERS = ["Purolator", "Canpar", "GLS", "UPS", "FedEx", "Poste Canada"];

export default function Carriers() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });
  const { data: fsStatus } = useQuery({ queryKey: ["flagship-status"], queryFn: async () => (await api.get("/flagship/status")).data, retry: false });

  const [shippingBase, setShippingBase] = useState("");
  const [shippingPerKg, setShippingPerKg] = useState("");
  const [shippingCarrier, setShippingCarrier] = useState("");
  const [shippingMargin, setShippingMargin] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [shipFrom, setShipFrom] = useState({ address: "", city: "", province: "", postal: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!business) return;
    setShippingBase(business.shipping_base != null ? String(business.shipping_base) : "7.99");
    setShippingPerKg(business.shipping_per_kg != null ? String(business.shipping_per_kg) : "2.5");
    setShippingCarrier(business.shipping_carrier ?? (CARRIERS_BY_COUNTRY[business.country]?.[0]?.id || ""));
    setShippingMargin(business.shipping_margin != null ? String(business.shipping_margin) : "0");
    setCarriers((business.shipping_carriers || []).map((c) => ({ _k: nextKey(), name: c.name || "", base: c.base ?? "", per_kg: c.per_kg ?? "" })));
    setShipFrom({ address: business.ship_from_address || "", city: business.ship_from_city || business.city || "", province: business.ship_from_province || "", postal: business.ship_from_postal || "" });
  }, [business]);

  const country = business?.country || "CA";
  const currency = business?.currency || COUNTRIES[country]?.currency || "CAD";

  const addCarrier = () => setCarriers([...carriers, { _k: nextKey(), name: "", base: "", per_kg: "" }]);
  const updCarrier = (i, k, v) => { const c = [...carriers]; c[i][k] = v; setCarriers(c); };
  const rmCarrier = (i) => setCarriers(carriers.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        shipping_carrier: shippingCarrier || "",
        shipping_margin: parseFloat(shippingMargin) || 0,
        shipping_carriers: carriers
          .filter((c) => (c.name || "").trim())
          .map((c) => ({ name: c.name.trim(), base: parseFloat(c.base) || 0, per_kg: parseFloat(c.per_kg) || 0 })),
      };
      if (!shippingCarrier) {
        payload.shipping_base = parseFloat(shippingBase) || 0;
        payload.shipping_per_kg = parseFloat(shippingPerKg) || 0;
      }
      if (country === "CA") {
        payload.ship_from_address = shipFrom.address.trim();
        payload.ship_from_city = shipFrom.city.trim();
        payload.ship_from_province = shipFrom.province.trim();
        payload.ship_from_postal = shipFrom.postal.trim();
      }
      await api.put("/business", payload);
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success(t("settings.storeUpdated"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 pt-6" data-testid="carriers-page">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1 flex items-center gap-2"><Truck className="h-6 w-6 text-[#1B4332]" /> {t("nav.carriers")}</h1>
      <p className="text-sm text-[#5C574F] mb-5">{t("carriers.subtitle")}</p>

      {/* FlagShip : livreurs en direct */}
      {country === "CA" && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-4" data-testid="flagship-origin-card">
          <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Truck className="h-5 w-5 text-[#1B4332]" /> {t("settings.flagshipTitle")}</h3>
          {fsStatus && (
            <div data-testid="flagship-status-banner" className={`mb-3 flex items-start gap-2 rounded-lg p-2.5 text-xs leading-relaxed ${fsStatus.configured ? "bg-[#F0F7F4] border border-[#1B4332]/15 text-[#1B4332]" : "bg-[#FFF4E6] border border-[#E85D04]/30 text-[#8a4a00]"}`}>
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {fsStatus.configured
                  ? (fsStatus.origin_set
                      ? "✅ FlagShip est actif. Vos clients verront les transporteurs en direct au checkout."
                      : "✅ FlagShip est actif sur le serveur. Renseignez votre adresse d'expédition ci-dessous pour des tarifs précis.")
                  : "⚠️ FlagShip n'est PAS configuré sur le serveur (clé FLAGSHIP_TOKEN absente de l'environnement de production). Ajoutez-la dans l'onglet Secrets du déploiement, puis redéployez — sinon contactez le support Emergent."}
              </span>
            </div>
          )}
          <p className="text-xs text-[#9c968a] mb-3">{t("settings.flagshipHint")}</p>
          <div className="mb-3 flex flex-wrap gap-1.5" data-testid="flagship-partners">
            {FLAGSHIP_PARTNERS.map((n) => (
              <span key={n} className="text-[11px] font-medium text-[#1B4332] bg-[#1B4332]/8 border border-[#1B4332]/15 px-2 py-0.5 rounded-full">{n}</span>
            ))}
          </div>
          <div className="space-y-2">
            <Field label={t("checkout.addressPh")}><input data-testid="flagship-address" value={shipFrom.address} onChange={(e) => setShipFrom({ ...shipFrom, address: e.target.value })} placeholder="1200 Bd Pie-XI Sud" className="input" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label={t("checkout.cityPh")}><input data-testid="flagship-city" value={shipFrom.city} onChange={(e) => setShipFrom({ ...shipFrom, city: e.target.value })} placeholder="Québec" className="input" /></Field>
              <Field label={t("checkout.provincePh")}><input data-testid="flagship-province" value={shipFrom.province} onChange={(e) => setShipFrom({ ...shipFrom, province: e.target.value })} placeholder="QC" className="input" /></Field>
            </div>
            <Field label={t("checkout.postalPh")}><input data-testid="flagship-postal" value={shipFrom.postal} onChange={(e) => setShipFrom({ ...shipFrom, postal: e.target.value })} placeholder="G3K 0W2" className="input" /></Field>
          </div>
          <div data-testid="flagship-activation-note" className="mt-3 flex items-start gap-2 rounded-lg bg-[#F0F7F4] border border-[#1B4332]/10 p-2.5">
            <Info className="h-4 w-4 text-[#1B4332] shrink-0 mt-0.5" />
            <p className="text-xs text-[#5C574F] leading-relaxed">{t("settings.flagshipActivation")}</p>
          </div>
          <button onClick={save} disabled={busy} data-testid="save-flagship-btn" className="w-full h-12 mt-3 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveShipping")}</button>
        </div>
      )}

      {/* Transporteur préréglé (grille publique) + tarif personnalisé */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-4" data-testid="shipping-formula-card">
        <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Truck className="h-5 w-5 text-[#0077B6]" /> {t("carriers.presetTitle")}</h3>
        <p className="text-xs text-[#9c968a] mb-4">{t("carriers.presetHint")}</p>
        <div className="space-y-2">
          {(CARRIERS_BY_COUNTRY[country] || []).map((c) => {
            const selected = shippingCarrier === c.id;
            return (
              <button key={c.id} type="button" disabled={c.soon} data-testid={`carrier-option-${c.id}`}
                onClick={() => !c.soon && setShippingCarrier(c.id)}
                className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${c.soon ? "opacity-50 cursor-not-allowed border-[#E5E0D8] bg-[#F9F6F0]" : selected ? "border-[#1B4332] bg-[#F0F7F4]" : "border-[#E5E0D8] bg-white hover:border-[#1B4332]/40"}`}>
                <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-[#1B4332]" : "border-[#cfc8ba]"}`}>{selected && <span className="h-2.5 w-2.5 rounded-full bg-[#1B4332]" />}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 font-semibold text-sm">{c.label}{c.soon && <span className="text-[10px] font-bold uppercase tracking-wide text-[#E85D04] bg-[#E85D04]/10 px-1.5 py-0.5 rounded">{t("settings.carrierSoon")}</span>}</span>
                  <span className="block text-xs text-[#9c968a]">{c.desc}</span>
                </span>
              </button>
            );
          })}
          <button type="button" data-testid="carrier-option-custom" onClick={() => setShippingCarrier("")}
            className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${shippingCarrier === "" ? "border-[#1B4332] bg-[#F0F7F4]" : "border-[#E5E0D8] bg-white hover:border-[#1B4332]/40"}`}>
            <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${shippingCarrier === "" ? "border-[#1B4332]" : "border-[#cfc8ba]"}`}>{shippingCarrier === "" && <span className="h-2.5 w-2.5 rounded-full bg-[#1B4332]" />}</span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-sm">{t("settings.carrierCustom")}</span>
              <span className="block text-xs text-[#9c968a]">{t("settings.carrierCustomDesc")}</span>
            </span>
          </button>
        </div>
        {shippingCarrier ? (
          <>
            <div data-testid="carrier-note" className="mt-3 flex items-start gap-2 rounded-lg bg-[#F0F7F4] border border-[#1B4332]/10 p-2.5">
              <Info className="h-4 w-4 text-[#1B4332] shrink-0 mt-0.5" />
              <p className="text-xs text-[#5C574F] leading-relaxed">{t("settings.carrierNote")}</p>
            </div>
            <div className="mt-3">
              <Field label={t("settings.carrierMargin", { cur: currency })}><input data-testid="shipping-margin" type="number" step="0.01" value={shippingMargin} onChange={(e) => setShippingMargin(e.target.value)} placeholder="0" className="input" /></Field>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label={t("settings.baseRate", { cur: currency })}><input data-testid="shipping-base" type="number" step="0.01" value={shippingBase} onChange={(e) => setShippingBase(e.target.value)} placeholder="7.99" className="input" /></Field>
              <Field label={t("settings.perKg", { cur: currency })}><input data-testid="shipping-per-kg" type="number" step="0.01" value={shippingPerKg} onChange={(e) => setShippingPerKg(e.target.value)} placeholder="2.5" className="input" /></Field>
            </div>
            <p className="text-[11px] text-[#9c968a] mt-2">{t("settings.shippingExample", { amount: money((parseFloat(shippingBase) || 0) + 2 * (parseFloat(shippingPerKg) || 0), currency) })}</p>
          </>
        )}
        <button onClick={save} disabled={busy} data-testid="save-shipping-btn" className="w-full h-12 mt-4 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveShipping")}</button>
      </div>

      {/* Transporteurs manuels (options client) */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-4" data-testid="my-carriers-card">
        <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Truck className="h-5 w-5 text-[#0077B6]" /> {t("settings.myCarriersTitle")}</h3>
        <p className="text-xs text-[#9c968a] mb-3">{t("settings.myCarriersHint")}</p>
        <div className="space-y-3">
          {carriers.map((c, i) => (
            <div key={c._k} className="rounded-xl border border-[#E5E0D8] p-3" data-testid={`carrier-row-${i}`}>
              <div className="flex gap-2 items-center mb-2">
                <input value={c.name} onChange={(e) => updCarrier(i, "name", e.target.value)} placeholder={t("settings.carrierNamePh")} className="flex-1 h-11 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" data-testid={`carrier-name-${i}`} />
                <button onClick={() => rmCarrier(i)} className="h-11 w-11 rounded-xl bg-[#D00000]/10 text-[#D00000] flex items-center justify-center shrink-0" data-testid={`carrier-remove-${i}`}>✕</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t("settings.baseRate", { cur: currency })}><input type="number" step="0.01" value={c.base} onChange={(e) => updCarrier(i, "base", e.target.value)} placeholder="7.99" className="input" data-testid={`carrier-base-${i}`} /></Field>
                <Field label={t("settings.perKg", { cur: currency })}><input type="number" step="0.01" value={c.per_kg} onChange={(e) => updCarrier(i, "per_kg", e.target.value)} placeholder="2.5" className="input" data-testid={`carrier-perkg-${i}`} /></Field>
              </div>
              <p className="text-[11px] text-[#9c968a] mt-1.5">{t("settings.shippingExample", { amount: money((parseFloat(c.base) || 0) + 2 * (parseFloat(c.per_kg) || 0), currency) })}</p>
            </div>
          ))}
          {carriers.length === 0 && <p className="text-xs text-[#9c968a]">{t("settings.noCarriers")}</p>}
        </div>
        <button onClick={addCarrier} data-testid="add-carrier-btn" className="w-full h-11 mt-3 rounded-xl border border-dashed border-[#E5E0D8] text-[#5C574F] font-medium text-sm">{t("settings.addCarrier")}</button>
        <button onClick={save} disabled={busy} data-testid="save-carriers-btn" className="w-full h-12 mt-3 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{t("settings.saveShipping")}</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">{label}</label>
      <div className="relative">{children}</div>
    </div>
  );
}
